import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { SubmittedInvoice } from '~/api/invoices/types';

import { useUser } from '~/api/user';
import { Button, Infotip, Separator } from '~/components';
import { creativeShelter } from '~/constants/creativeShelter';
import { isApproved } from '~/features/documents';
import { useIntl } from '~/hooks/useIntl';

import { PaymentStatusTag } from '../PaymentStatusTag';
import { breakdownActionStyle } from './InvoiceSplit.css';
import { InvoiceSplitCard } from './InvoiceSplitCard';

type Props = {
  invoice: SubmittedInvoice;
  onDownload: () => void;
};

export const FcInvoiceCard = ({ invoice, onDownload }: Props) => {
  const { data: user } = useUser();

  const { formatCurrency, formatDate } = useIntl();
  const { t } = useTranslation(['invoices', 'lines']);

  const { calculationData } = invoice;
  const performanceSubtotal = calculationData.fcPerformanceExclVat;
  const vat6 = calculationData.fcVatAmounts[0].amount;
  const vat21 = calculationData.fcVatAmounts[1].amount;
  const performanceTotal = performanceSubtotal + vat6 + vat21;
  const copyrightSubtotal = calculationData.fcCopyrightExclVat;
  const withholdingTax = calculationData.fcWithholdingTax;
  const copyrightTotal = copyrightSubtotal - withholdingTax;

  return (
    <InvoiceSplitCard.Root>
      <InvoiceSplitCard.Overview>
        <InvoiceSplitCard.Header
          tag={
            isApproved(invoice) && (
              <PaymentStatusTag status={invoice.status === 'Credited' ? 'credited' : invoice.fcPaidDate !== null ? 'fcPaid' : 'notPaid'} />
            )
          }
        >
          {invoice.fcEntryNumber !== null
            ? `${t('invoices:details.split.fc.eyebrow')} | ${invoice.fcEntryNumber}`
            : t('invoices:details.split.fc.eyebrow')}
        </InvoiceSplitCard.Header>

        <InvoiceSplitCard.Title from={user.companyName} to={creativeShelter.companyName} />

        <InvoiceSplitCard.Description>{t('invoices:details.split.fc.description')}</InvoiceSplitCard.Description>

        <InvoiceSplitCard.Highlight label={t('invoices:details.split.fc.highlight')}>
          {`${formatCurrency(performanceTotal)} & ${formatCurrency(copyrightTotal)}`}
        </InvoiceSplitCard.Highlight>

        <InvoiceSplitCard.Rows>
          <InvoiceSplitCard.Row label={t('invoices:details.paymentInfo.dueDate')}>{formatDate(invoice.dueDate)}</InvoiceSplitCard.Row>

          {invoice.fcPaidDate !== null ? (
            <InvoiceSplitCard.Row label={t('invoices:details.paymentInfo.paidDate')}>{formatDate(invoice.fcPaidDate)}</InvoiceSplitCard.Row>
          ) : (
            isApproved(invoice) &&
            invoice.status !== 'Credited' && (
              <InvoiceSplitCard.Row
                infotip={<Infotip>{t('invoices:details.paymentInfo.expectedPaidDateTooltip')}</Infotip>}
                label={t('invoices:details.paymentInfo.expectedPaidDate')}
              >
                {invoice.coPaidDate !== null ? formatDate(dayjs(invoice.coPaidDate).nextBusinessDay().toDate()) : '–'}
              </InvoiceSplitCard.Row>
            )
          )}

          {invoice.fcTransportType !== null && (
            <InvoiceSplitCard.Row label={t('invoices:details.paymentInfo.transportType.label')}>
              {t(`invoices:details.paymentInfo.transportType.options.${invoice.fcTransportType}`)}
            </InvoiceSplitCard.Row>
          )}
        </InvoiceSplitCard.Rows>
      </InvoiceSplitCard.Overview>

      <InvoiceSplitCard.Breakdown>
        <InvoiceSplitCard.AmountGroup label={t('invoices:details.split.performanceGroup')}>
          <InvoiceSplitCard.Amount label={t('invoices:details.split.amountExclVat')}>
            {formatCurrency(performanceSubtotal)}
          </InvoiceSplitCard.Amount>

          {vat21 > 0 && (
            <InvoiceSplitCard.Amount label={t('lines:vatPercentage', { percentage: 21 })}>{formatCurrency(vat21)}</InvoiceSplitCard.Amount>
          )}

          {vat6 > 0 && (
            <InvoiceSplitCard.Amount label={t('lines:vatPercentage', { percentage: 6 })}>{formatCurrency(vat6)}</InvoiceSplitCard.Amount>
          )}

          <InvoiceSplitCard.Amount isTotal label={t('invoices:details.split.totalInclVat')}>
            {formatCurrency(performanceTotal)}
          </InvoiceSplitCard.Amount>
        </InvoiceSplitCard.AmountGroup>

        <Separator text="&" />

        <InvoiceSplitCard.AmountGroup label={t('invoices:details.split.copyrightGroup')}>
          <InvoiceSplitCard.Amount label={t('invoices:details.split.amount')}>{formatCurrency(copyrightSubtotal)}</InvoiceSplitCard.Amount>

          <InvoiceSplitCard.Amount label={t('invoices:details.paymentInfo.withholdingTax')}>
            {formatCurrency(withholdingTax * -1)}
          </InvoiceSplitCard.Amount>

          <InvoiceSplitCard.Amount isTotal label={t('invoices:details.split.copyrightTotal')}>
            {formatCurrency(copyrightTotal)}
          </InvoiceSplitCard.Amount>
        </InvoiceSplitCard.AmountGroup>

        {isApproved(invoice) && (
          <>
            <Separator />
            <Button className={breakdownActionStyle} emphasis="tertiary" icon="ArrowRight" iconPlacement="right" onClick={onDownload}>
              {t('invoices:details.split.fc.download')}
            </Button>
          </>
        )}
      </InvoiceSplitCard.Breakdown>
    </InvoiceSplitCard.Root>
  );
};
